import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { customDateTimestampToDisplay } from 'utils/dateHelpers';
import PricePointBarChart from './PricePointBarChart';
import {
    fetchCurrency,
    fetchUnit,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
} from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import Threshold from './Threshold';
import Button from 'components/atoms/Button';
import Legend from 'components/molecules/Legend';
import { commonClassName } from './PromoScorecard';
import { yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { useFetchPricePointGraph } from 'hooks/pricePoint/useFetchGraphData';
import Barchart from 'components/molecules/Barchart';
import { chartHeight, yAxisWidth } from 'components/template/PriceLadder/BarPointChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { FilterContextData } from 'contexts/FilterContext';
import { colorConstants } from 'config/colorConstants';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { pricePointChartDownload } from 'parser/graphToExcelDownloadParser';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PRICE_POINT_CHART_CLICKED, PRICE_POINT_REDEFINE_THRESHOLD_CLICKED } from 'utils/mixpanelEventNames';
import { ConfigKeys } from 'utils/Enums';

const pricePointGraphWrapper = 'pricePointGraphWrapper';

interface props {
    selectPPG: any;
    currentPeriod?: any;
    subTileState: any;
    priceType: string;
    yAxisDropdownData: any;
    data: any;
    error: any;
    loading: boolean;
    selectedGraphItem: any;
    setSelectedGraphItem: Function;
    priceRange: null | [number, number];
    defaultPriceRange: any;
    setPriceRange: Function;
    batchSize: number;
    setBatchSize: Function;
    fetchPricePointGraph: Function;
    tiles: any;
    setTiles: Function;
    setCustomThresholdSwitch: Function;
    customThresholdSwitch: boolean;
}
const PricePointSection: FC<props> = ({
    selectedGraphItem,
    setSelectedGraphItem,
    selectPPG,
    currentPeriod,
    subTileState,
    priceType,
    yAxisDropdownData,
    data,
    error,
    loading,
    priceRange,
    setPriceRange,
    batchSize,
    setBatchSize,
    fetchPricePointGraph,
    tiles,
    setTiles,
    customThresholdSwitch,
    setCustomThresholdSwitch,
    defaultPriceRange,
}) => {
    let { appConfig } = useContext(UserContext);
    const pricePointConfig = getConfig('pricePoint', appConfig);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForPercentage, decimalForNonPercentage } = commonConfig;

    const contributionThreshold = pricePointConfig?.contributionThreshold;
    const chartCopyRef = useRef<any>();

    const [showThreshold, setShowThreshold] = useState(false);
    //graph main axis width
    const containerWidth = useContainerWidth({
        containerId: pricePointGraphWrapper,
        buffer: yAxisWidth * 1 + 34 * 2,
    });

    //CONTEXT
    const { timeSelectionLabel, timePeriodSelection, allCheckedAttribute } = useContext(FilterContextData);

    const displayTimeLabel = useMemo(() => {
        if (String(timeSelectionLabel)?.toLowerCase() === 'custom date' && timePeriodSelection) {
            const parsed = typeof timePeriodSelection === 'string' ? JSON.parse(timePeriodSelection) : timePeriodSelection;
            if (parsed?.start_timestamp && parsed?.end_timestamp) {
                const start = customDateTimestampToDisplay(parsed.start_timestamp, commonConfig?.dateFormat);
                const end = customDateTimestampToDisplay(parsed.end_timestamp, commonConfig?.dateFormat);
                return `${start} - ${end}`;
            }
        }
        return String(timeSelectionLabel)?.toUpperCase();
    }, [timeSelectionLabel, timePeriodSelection]);
    const onshowtileschange = () => {
        setShowThreshold(true);
        //mixpanel event
        trackEventMixpanel(PRICE_POINT_REDEFINE_THRESHOLD_CLICKED);
    };

    const legendsData = useMemo(() => {
        const reversedColors = [...pricePointConfig.graphColorScheme]?.reverse();
        if (data && data?.legends?.length > 0) {
            return data?.legends?.map((item: any, index: number) => {
                return {
                    color: reversedColors?.[index],
                    label: item,
                };
            });
        }
        return [];
    }, [data]);
    const currency = fetchCurrency(appConfig) || '';
    const graphExcelData = useMemo(() => {
        return pricePointChartDownload(data, currency);
    }, [data]);
    const currencyText = fetchUnit(appConfig, 'currencyText');
    let sumOfGraphWidth = 0; //taking the sum og individual graph widths for tooltip positioning
    return (
        <div ref={chartCopyRef} className={commonClassName}>
            <div className="flex w-full justify-between">
                {timeSelectionLabel && selectPPG?.label && (
                    <p className="font-lg text-headerColor font-medium">
                        {subTileState?.xAxisLabel} by {selectPPG?.label} in {displayTimeLabel}
                    </p>
                )}

                <Threshold
                    subTileState={subTileState}
                    defaultPriceRange={defaultPriceRange}
                    switchValue={customThresholdSwitch}
                    setSwitchValue={setCustomThresholdSwitch}
                    onApplyButton={fetchPricePointGraph}
                    batchSize={batchSize}
                    setBatchSize={setBatchSize}
                    priceRange={priceRange}
                    setPriceRange={setPriceRange}
                    tiles={tiles}
                    setTiles={setTiles}
                    showThreshold={showThreshold}
                    setShowThreshold={setShowThreshold}
                />
                <div className="flex gap-2 items-center">
                    {priceRange && defaultPriceRange && (
                        <Button title={'Redefine Threshold'} onClick={onshowtileschange} />
                    )}
                    {!loading && (
                        <div className="flex justify-center items-center gap-2">
                            <CopyToClipBoard refOfComponent={chartCopyRef} />
                            <DownloadExcel
                                title={'PricePointChart'}
                                excelData={graphExcelData.data}
                                headers={graphExcelData.headers}
                            />
                        </div>
                    )}
                </div>
            </div>
            {loading ? (
                <div style={{ height: chartHeight + 50 }}>
                    <Loader />
                </div>
            ) : error ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: chartHeight + 50,
                    }}
                >
                    <ErrorBox msg={String(error)} />
                </div>
            ) : data?.chartData ? (
                <>
                    <div className="my-4 flex justify-between gap-1 items-center">
                        <p className={`${yAxisLabelClass} w-[160px]`}>
                            {yAxisDropdownData?.label} share by {selectPPG?.label}
                        </p>
                        <Legend data={legendsData} containerClassName="!justify-start" />
                        <p className="w-[80px]"></p>
                    </div>

                    <div className="mt-5 flex">
                        {/* Y1 AXIS */}
                        <Barchart
                            // padding={[0, 0, 0, 48]}
                            padding={[0, 0, 0, 0]}
                            additionalStyle={{ marginTop: 22 }}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`price-point-y1`}
                            fields={['category', 'sales']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            minBound={data?.min}
                            maxBound={data?.max}
                            width={yAxisWidth}
                            colorKey={'color'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, decimalForPercentage) + '%';
                            }}
                            // Yline
                            YGrid={false}
                        />
                        <div
                            className="flex gap-1 overflow-x-auto"
                            style={{ maxWidth: '100%' }}
                            id={pricePointGraphWrapper}
                        >
                            {Object.keys(data?.chartData || {})?.map((item: string, index: any) => {
                                const width =
                                    Math.floor(
                                        Math.max((data?.chartData?.[item]?.count / 100) * containerWidth - 4, 20),
                                    ) - 4;
                                sumOfGraphWidth += width;
                                return data?.chartData?.[item] ? (
                                    <div className="mb-2">
                                        <div
                                            style={{ maxWidth: width }}
                                            className="font-xs h-[22px] text-center overflow-x-hidden"
                                        >
                                            {data?.chartData?.[item]?.count >= contributionThreshold
                                                ? formatNumWithKMBAlt(
                                                      data?.chartData?.[item]?.count,
                                                      decimalForPercentage,
                                                  ) + '%'
                                                : ''}
                                        </div>
                                        <div
                                            onClick={() => {
                                                if (
                                                    selectedGraphItem?.price ===
                                                    data?.chartData?.[item]?.data?.[0]?.price
                                                )
                                                    setSelectedGraphItem(null);
                                                else {
                                                    const price = data?.chartData?.[item]?.data?.[0]?.price;
                                                    const range = data?.chartData?.[item]?.range;
                                                    setSelectedGraphItem({
                                                        price,
                                                        color: colorConstants.fontGrey,
                                                        range,
                                                    });
                                                    //mixpanel event
                                                    trackEventMixpanel(PRICE_POINT_CHART_CLICKED, {
                                                        price,
                                                        range,
                                                        tabName: subTileState?.label,
                                                    });
                                                }
                                            }}
                                            style={{
                                                border:
                                                    selectedGraphItem?.price ===
                                                    data?.chartData?.[item]?.data?.[0]?.price
                                                        ? `1px solid ${selectedGraphItem?.color}`
                                                        : '',
                                            }}
                                        >
                                            <PricePointBarChart
                                                xTitle={data?.chartData?.[item]?.data?.[0]?.price}
                                                selectedGraphItem={selectedGraphItem}
                                                onClick={setSelectedGraphItem}
                                                key={index + 'i'}
                                                tooltipPosition={
                                                    sumOfGraphWidth > containerWidth / 2 ? 'right' : 'left'
                                                }
                                                chartContainer={'price-point-main' + index}
                                                width={width}
                                                height={chartHeight}
                                                dataset={data?.chartData?.[item]?.data}
                                                colorScheme={pricePointConfig.graphColorScheme}
                                                decimalForPercentage={decimalForPercentage}
                                                decimalForNonPercentage={decimalForNonPercentage}
                                            />
                                        </div>

                                        <div
                                            style={{ maxWidth: width }}
                                            title={`$${data?.chartData?.[item]?.data?.[0]?.price}`}
                                            className="text-right overflow-x-hidden font-xs"
                                        >
                                            {currency}
                                            {data?.chartData?.[item]?.data?.[0]?.price}
                                        </div>
                                    </div>
                                ) : (
                                    <></>
                                );
                            })}
                        </div>
                    </div>
                    <p className={`${yAxisLabelClass} mt-2 flex justify-center`}>
                        {priceType} {subTileState?.xAxisLabel}
                    </p>
                </>
            ) : (
                <div style={{ height: chartHeight + 50 }}></div>
            )}
        </div>
    );
};
export default PricePointSection;
